import React = require("react");
import {HierarchyNode, HierarchySelect} from "./HierarchySelect";
import {defaultTheme} from "@vebox/etui-themes";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../index";

interface HierarchyFilterData {
    configuration: HierarchyFilterConfiguration;
    nodes: HierarchyNode[];
}

interface HierarchyFilterConfiguration {
    filterLabel: string;
    noMatchesText: string;
    placeholderText: string;
    selectedElementsText: string;
    selectedElementText: string;
}

interface HierarchyFilterProps {
    nodesUrl: string;
    onChange: (currentNode: HierarchyNode, selectedNodes: HierarchyNode[]) => any;
    defaultValues?: string[];
}

interface HierarchyFilterState {
    configuration: HierarchyFilterConfiguration;
    hierarchyNodes: HierarchyNode[];
    selectedNodes: HierarchyNode[];
    renderSelect: boolean;
}

interface HierarchySelectStatusCounterProps {
    text: string;
    title: string;
    count: number;
    onDeselectAll: () => any;
}

const HierarchySelectStatusCounter: React.SFC<HierarchySelectStatusCounterProps> = (props) => {
    let resetter = null;
    if (props.count > 0) {
        resetter = (
            <div className="hierarchy-filter-counter__cancel-button" onClick={props.onDeselectAll}>
                <span>X</span>
            </div>
        );
    } else {
        return null;
    }

    return (
        <div className="hierarchy-filter-counter">
            <p title={props.title}>{props.count} {props.text}</p>
            {resetter}
        </div>
    );

};

export class HierarchyFilter extends React.Component<HierarchyFilterProps, HierarchyFilterState> {

    private static getSelectedElementsTitle(selectedNodes: HierarchyNode[]) {
        if (selectedNodes == null || selectedNodes.length === 0) {
            return null;
        }
        let result: string = "";
        for (let i = 0; i < selectedNodes.length; i++) {
            if (i === 0) {
                result += selectedNodes[i].label;
            } else {
                result += ", " + selectedNodes[i].label;
            }
        }
        return result;
    }

    constructor(props: HierarchyFilterProps) {
        super(props);
        this.onChange = this.onChange.bind(this);
        this.onDeselectAll = this.onDeselectAll.bind(this);
        this.state = {configuration: null, hierarchyNodes: [], selectedNodes: [], renderSelect: false};
    }

    public componentWillUpdate(nextProps: HierarchyFilterProps, nextState: HierarchyFilterState) {
        if (this.state.renderSelect !== nextState.renderSelect
            && nextState.renderSelect
            && this.props.onChange) {
            this.props.onChange(null, []);
        }
    }

    public componentDidMount() {
        this.loadNodes();
    }

    public render() {
        if (this.state.hierarchyNodes.length === 0) {
            return null;
        }
        let title = <p className="hierarchy-filter__title">{this.state.configuration.filterLabel}</p>;
        if (this.state.selectedNodes && this.state.selectedNodes.length > 0) {
            title = (
                <p className="hierarchy-filter__title filtersGroupTitleEnlight">
                    {this.state.configuration.filterLabel}
                </p>
            );
        }
        let counterText = this.state.configuration.selectedElementsText;
        if (this.state.selectedNodes && this.state.selectedNodes.length === 1) {
            counterText = this.state.configuration.selectedElementText;
        }
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div className="hierarchy-filter">
                    {title}
                    <HierarchySelectStatusCounter
                        text={counterText}
                        title={HierarchyFilter.getSelectedElementsTitle(this.state.selectedNodes)}
                        count={this.state.selectedNodes ? this.state.selectedNodes.length : 0}
                        onDeselectAll={this.onDeselectAll}
                    />
                    <HierarchySelect
                        onChange={this.onChange as any}
                        hierarchyNodes={
                            this.state.hierarchyNodes.map((element => {
                                element.disabled = element.children.length === 0;
                                return element;
                            }))
                        }
                        render={this.state.renderSelect}
                        noMatchesText={this.state.configuration.noMatchesText}
                        placeholderText={this.state.configuration.placeholderText}
                        defaultValues={this.props.defaultValues}
                    />
                </div>
            </ThemeProvider>
        );
    }

    private loadNodes(): void {
        Ajax.ajaxRequestForJson(
            {
                url: this.props.nodesUrl,
                type: "GET",
                success: (response: HierarchyFilterData) => {

                    const selectedByDefault = response.nodes.map((node) => {
                        return this.selectedByDefault(this.props.defaultValues || [], node);
                    }).filter((x) => x !== undefined);

                    this.setState({
                            configuration: response.configuration,
                            hierarchyNodes: this.sortHierarchyNodes(response.nodes),
                            renderSelect: true,
                            selectedNodes: selectedByDefault.length > 0 ? selectedByDefault : null,
                        },
                        () => {
                            if (selectedByDefault.length > 0) {
                                this.onChange(selectedByDefault[0], selectedByDefault);
                            }
                        },
                    );
                },
            },
        );
    }

    private sortHierarchyNodes(nodes: HierarchyNode[]): HierarchyNode[] {
        if (nodes == null || nodes.length === 0) {
            return nodes;
        }
        for (const node of nodes) {
            node.children = this.sortHierarchyNodes(node.children);
        }
        nodes.sort((n1, n2) => n1.label.localeCompare(n2.label));
        return nodes;
    }

    private onChange(currentNode: HierarchyNode, selectedNodes: HierarchyNode[]): void {
        if (this.props.onChange) {
            this.props.onChange(currentNode, selectedNodes);
        }
        this.setState({selectedNodes, renderSelect: false});
    }

    private onDeselectAll(): void {
        this.setState({selectedNodes: [], renderSelect: true});
    }

    private selectedByDefault = (defaultValues: string[], node: HierarchyNode): HierarchyNode => {
        if (defaultValues.indexOf(node.value) > -1) {
            return node;
        }

        for (const child of node.children) {
            const r = this.selectedByDefault(defaultValues, child);
            if (r !== undefined) {
                return r;
            }
        }

        return undefined;
    }
}
